import { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogTitle, IconButton, DialogContent, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getBankLinking, getIntegrationKey, postAccountRelinking, reconnectSophtron, postSophtronEvent } from 'lib/api/banks';
import { AuthContext } from 'context/auth-context';
import { BankLinkSecondStep } from './BankLinkSecondStep';

export const Sophtron = ({ shown, setShown, onComplete, reconnect = false }) => {
    const [widgetURL, setWidgetURL] = useState('');
    const [accounts, setAccounts] = useState([]);
    const [hasPrimaryAccount, setHasPrimaryAccount] = useState(false);
    const sophtronStatus = useRef();
    const { userInfo } = useContext(AuthContext);
    const SophtronClient = window.sophtron;

    const finishSophtronLogin = event => {
        onSophtronEvent(event);
        if (reconnect) {
            postAccountRelinking({
                userInstitution_id: event.userInstitutionId
            }).then(() => {
                onComplete();
            });
            return;
        }
        const accountsArray = event.accounts;
        const filteredArray = accountsArray.filter(
            account => account.AccountType === 'Checking' || account.AccountType === 'Savings'
        );
        if (!filteredArray) {
            return;
        }
        setAccounts(filteredArray);
        SophtronClient.destroy();
    };

    const onSophtronEvent = event => {
        const eventBody = {
            step: event.step || event._type,
            job_id: event.jobId || event.JobID,
            user_institution_id: event.userInstitutionId,
            institution_id: event.institutionId,
            event_data: event
        };
        postSophtronEvent(eventBody).catch(console.error);
    };

    const openSophtron = response => {
        let action = 'BankAuth';
        const SophtronConfig = {
            env: 'prod',
            partner: 'paystand',
            integration_key: response.integration_key,
            request_id: response.requestId,
            onShow: onSophtronEvent,
            onInit: onSophtronEvent,
            onClose: onSophtronEvent,
            onSelectBank: onSophtronEvent,
            onLogin: onSophtronEvent,
            onLoginSuccess: onSophtronEvent,
            onMfa: onSophtronEvent,
            onFinish: e => finishSophtronLogin(e),
            onError: onSophtronEvent
            /*inistitution_id: "", //optional, pre select a bank to skip the bank search step
        routing_number: "", //optional, pre select a bank to skip the bank search step
        userInistitution_id: "", //if action is Refresh, it's required as the id of the account being refreshed*/
        };
        if (reconnect) {
            SophtronConfig.userInstitution_id = response.userInstitution_id;
            action = 'Refresh';
        }
        if (process.env.REACT_APP_API_URL !== process.env.REACT_APP_API_URL_PRODUCTION) {
            SophtronConfig.env = 'preview';
        }
        SophtronClient.init(action, SophtronConfig, true);
        SophtronClient.show();
    };

    const fetchBankLinking = useCallback(async () => {
        try {
            const response = await getBankLinking();
            setAccounts(response.accounts);
            if (response.has_primary) {
                setHasPrimaryAccount(false);
                return;
            }
            setHasPrimaryAccount(true);
        } catch (error) {
            console.error(error);
        }
    }, []);

    const doSophtronReconnect = useCallback(async () => {
        try {
            const response = await reconnectSophtron(userInfo.bank_need_reconnect);
            setWidgetURL(response.widgetUrl);
            openSophtron(response);
        } catch (error) {
            console.error(error);
        }
    }, [userInfo.bank_need_reconnect]);

    const fetchSophtronIntegrationKey = useCallback(async () => {
        try {
            const response = await getIntegrationKey();
            setWidgetURL(response.widgetUrl);
            openSophtron(response);
        } catch (error) {
            console.error(error);
        }
    }, []);
    useEffect(() => {
        if (userInfo.bank_pending_add) {
            fetchBankLinking();
            return;
        } else if (reconnect) {
            doSophtronReconnect();
            return;
        } else {
            fetchSophtronIntegrationKey();
        }
    }, [doSophtronReconnect, fetchBankLinking, fetchSophtronIntegrationKey, reconnect, userInfo.bank_pending_add]);

    const handleClose = () => {
        setShown(false);
        clearInterval(sophtronStatus.current);
    };

    const Iframe = ({ source }) => {
        return (
            <>
                {accounts.length > 0 ? (
                    <Dialog
                        open={shown}
                        onClose={(event, reason) => {
                            if (reason !== 'backdropClick') {
                                handleClose();
                            }
                        }}>
                        <Box sx={{ position: 'relative' }}>
                            {accounts.length > 0 && <DialogTitle>Accounts</DialogTitle>}
                            <IconButton
                                aria-label="close"
                                onClick={handleClose}
                                sx={{
                                    position: 'absolute',
                                    right: 12,
                                    top: 8,
                                    color: theme => theme.palette.grey[500]
                                }}>
                                <CloseIcon />
                            </IconButton>
                            <DialogContent>
                                <Box>
                                    <BankLinkSecondStep
                                        setShown={setShown}
                                        has_primary={hasPrimaryAccount}
                                        accounts={accounts}
                                        onComplete={onComplete}
                                    />
                                </Box>
                            </DialogContent>
                        </Box>
                    </Dialog>
                ) : null}
            </>
        );
    };

    return <Iframe source={widgetURL} />;
};
